// @ts-nocheck
import { CommonHelperFunctions, IResetPasswordWithTokenDto } from "@kuika/kuika-cl-model";
import Form, { Field } from "rc-field-form";
import * as React from "react";
import { UserService } from "../../services/user-service";
import { IWithHistoryProp, withHistory } from "../../shared/hoc/with-history";
import { KMainFunctions } from "../../shared/utility/main-functions";
import { TitleManager } from "../../shared/utility/title-manager";
import AuthCreateNewPasswordSvg from "../../svgs/auth-create-new-password.svg";
import AuthVerifyEmailSvg from "../../svgs/auth-verify-email.svg";
import KuikaButton, {
  KUIKA_BUTTON_SUCCESS_HOLD_MS,
  type IKuikaButtonStatusLabels,
  type KuikaButtonStatus
} from "../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../ui-kit/kuika-input/kuika-input";
import KuikaModalIconWrapper from "../ui-kit/kuika-modal-icon-wrapper";
import KuikaSvg from "../ui-kit/kuika-svg/kuika-svg";
import KuikaH1 from "../ui-kit/typography/H1/H1";
import KuikaH4 from "../ui-kit/typography/H4/H4";
import KuikaSmallMediumText from "../ui-kit/typography/SmallMediumText/SmallMediumText";
import KuikaText from "../ui-kit/typography/Text/Text";
import { AuthLogo } from "./auth-logo";

interface IResetPasswordFormValues {
  NewPassword: string;
  confirmPassword: string;
  email?: string;
}

enum ResetStatus {
  None = 1,
  Success = 2,
  Error = 3
}

export interface IResetPasswordState {
  resetPasswordErrorMessage: string;
  resetStatus: ResetStatus;
  /**
   * The success panel is on screen. It trails `resetStatus` by the ✓ hold, so the button's own
   * confirmation is seen before the panel replaces the form.
   */
  isSuccessShown: boolean;
  isLoading: boolean;
  showPassword: boolean;
  showConfirmPassword: boolean;
  countdown: number;
  newPassword: string;
  confirmPassword: string;
}

export interface IResetPasswordProps {}

/** What the change button says while the new password is saved and once it has been. */
const CHANGE_PASSWORD_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Changing", success: "Changed" };

class ResetPassword extends React.PureComponent<IResetPasswordProps & IWithHistoryProp, IResetPasswordState> {
  private tokenString = "";

  private username = "";

  timer: NodeJS.Timeout | null = null;

  private successRevealTimer: number | undefined = undefined;

  constructor(props: IResetPasswordProps & IWithHistoryProp) {
    super(props);

    if (!CommonHelperFunctions.isRedirectedBy("signup")) {
      this.props.navigator.gotoRoot();
    }

    this.state = {
      resetPasswordErrorMessage: "",
      resetStatus: ResetStatus.None,
      isSuccessShown: false,
      isLoading: false,
      showPassword: false,
      showConfirmPassword: false,
      countdown: 5,
      newPassword: "",
      confirmPassword: ""
    };
  }

  componentDidMount = () => {
    this.getState();
    TitleManager.setPageTitle("Set a New Password | Kuika", "Main Low Code Set New Password");
    TitleManager.setPageMeta(
      "Create a new password for your Kuika account and continue building with the low-code platform."
    );
  };

  /**
   * The password was changed: the ✓ goes up on the button first and the success panel follows it.
   * Done here, off the status the request handler already sets, so the handler — and the sign-out
   * countdown it starts — are left exactly as they were.
   */
  componentDidUpdate(_prevProps: IResetPasswordProps & IWithHistoryProp, prevState: IResetPasswordState): void {
    if (this.state.resetStatus === ResetStatus.Success && prevState.resetStatus !== ResetStatus.Success) {
      this.successRevealTimer = window.setTimeout((): void => {
        this.successRevealTimer = undefined;
        this.setState({ isSuccessShown: true });
      }, KUIKA_BUTTON_SUCCESS_HOLD_MS);
    }
  }

  componentWillUnmount() {
    TitleManager.clearPageTitle();
    if (this.timer) {
      clearInterval(this.timer);
    }
    if (this.successRevealTimer !== undefined) {
      window.clearTimeout(this.successRevealTimer);
    }
  }

  private getSubmitStatus = (): KuikaButtonStatus => {
    if (this.state.isLoading) {
      return "loading";
    }
    if (this.state.resetStatus === ResetStatus.Success) {
      return "success";
    }
    return "idle";
  };

  /** The button stays clickable while its ✓ is up, so a second change is kept out here. */
  private handleFinish = (data: IResetPasswordFormValues): void => {
    if (this.state.isLoading || this.state.resetStatus === ResetStatus.Success) {
      return;
    }
    void this.onSubmit(data);
  };

  getState = () => {
    // Use URLSearchParams for token (safe)
    const searchParams = new URLSearchParams(this.props.navigator.location.search);
    this.tokenString = searchParams.get("token") ?? "";

    const search = this.props.location.search;
    const userMatch = search.match(/[?&]user=([^&]*)/);
    if (userMatch) {
      this.username = decodeURIComponent(userMatch[1].replace(/\+/g, "%2B"));
    } else {
      this.username = "";
    }
  };

  goToLogin = () => {
    this.props.navigator.gotoLogin();
  };

  onSubmit = async (data: any) => {
    this.setState({ isLoading: true, resetPasswordErrorMessage: "" });
    try {
      if (data.confirmPassword !== data.NewPassword) {
        this.setState({
          resetPasswordErrorMessage: "Your second password doesn't match with the first one.",
          isLoading: false
        });
        return;
      }
      const formData = {} as IResetPasswordWithTokenDto;
      const email = this.username || data.email;
      formData.token = this.tokenString;
      formData.UserName = email;
      formData.newPassword = data.confirmPassword;
      UserService.ResetPasswordWithToken(formData)
        .then(
          async (res) => {
            if (res && res.status === 200) {
              this.setState(
                { resetPasswordErrorMessage: "", resetStatus: ResetStatus.Success, isLoading: false, countdown: 5 },
                () => {
                  this.timer = setInterval(() => {
                    this.setState(
                      (prevState) => ({ countdown: prevState.countdown - 1 }),
                      () => {
                        if (this.state.countdown <= 0) {
                          if (this.timer) clearInterval(this.timer);
                          UserService.Logout(undefined, true);
                        }
                      }
                    );
                  }, 1000);
                }
              );
            } else {
              this.setState({ isLoading: false });
            }
          },
          (error) => {
            this.setState({ isLoading: false });
            if (error.response?.data?.message) {
              this.setState({
                resetPasswordErrorMessage: error.response.data.message,
                resetStatus: ResetStatus.Error,
                isLoading: false
              });
            }
          }
        )
        .catch((res) => {
          this.setState({ isLoading: false });
        });
    } catch (error) {
      this.setState({ isLoading: false });
      KMainFunctions.exceptionHandler(error);
    }
  };

  render() {
    const userEmail = UserService.getEmailToResetPassword();
    const isMainAI = KMainFunctions.isMainAI();

    return (
      <div className="email-confirmation">
        <div className="email-confirmation__content">
          <div className="email-confirmation__top-section">
            <AuthLogo />

            <div className="email-confirmation__check-inbox-card">
              {this.state.resetStatus === ResetStatus.Success && this.state.isSuccessShown ? (
                <>
                  <div className="email-confirmation__title-row">
                    <AuthVerifyEmailSvg />
                    <KuikaH4>Success</KuikaH4>
                  </div>

                  <div className="email-confirmation__line" />

                  <div className="email-confirmation__countdown-wrapper">
                    <KuikaH1 style={{ color: "var(--dark-8)", margin: 0 }}>{this.state.countdown}</KuikaH1>
                  </div>

                  <KuikaText className="email-confirmation__message-text">
                    Your password has been successfully changed. You are being redirected to sign in.
                  </KuikaText>
                </>
              ) : this.state.resetStatus === ResetStatus.Error ? (
                <>
                  <div className="email-confirmation__title-row">
                    <KuikaModalIconWrapper iconName="alert-triangle" intent="destructive" />
                    <KuikaH4>Error</KuikaH4>
                  </div>

                  <KuikaText className="email-confirmation__message-text" style={{ color: "var(--red-5)" }}>
                    {this.state.resetPasswordErrorMessage}
                  </KuikaText>

                  <div style={{ display: "flex", justifyContent: "center", marginTop: "16px" }}>
                    <KuikaButton type="tertiary" size="lg" uppercase onClick={this.goToLogin} style={{ width: "100%" }}>
                      Return to sign in
                    </KuikaButton>
                  </div>
                </>
              ) : (
                <>
                  <div className="email-confirmation__title-row">
                    <AuthCreateNewPasswordSvg />
                    <KuikaH4>Create new password</KuikaH4>
                  </div>

                  <Form
                    onFinish={this.handleFinish}
                    initialValues={{ email: userEmail }}
                    style={{ width: "100%", display: "flex", flexDirection: "column", gap: "24px" }}
                  >
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <Field
                        name="NewPassword"
                        rules={[
                          { required: true, message: "Please enter your password!" },
                          { min: 8, message: "Password length must be at least 8." },
                          { pattern: /(?=.*[a-z])/, message: "Password must contain at least 1 lowercase characters." },
                          { pattern: /(?=.*[A-Z])/, message: "Password must contain at least 1 uppercase characters." },
                          {
                            pattern: /(?=.*[!@#$%^&*(),.?":{}|<>\-_])/,
                            message: "Password must contain at least 1 special characters."
                          },
                          {
                            validator: (_, value) => {
                              if (value && /(0123|1234|2345|3456|4567|5678|6789|7890)/.test(value)) {
                                return Promise.reject(
                                  new Error("Password must not contain more than 3 sequential digits.")
                                );
                              }
                              return Promise.resolve();
                            }
                          }
                        ]}
                      >
                        {(control, meta) => (
                          <div style={{ width: "100%" }}>
                            <KuikaInput
                              {...control}
                              type={this.state.showPassword ? "text" : "password"}
                              leftIcon={<KuikaSvg name="lock" />}
                              placeholder="New password"
                              size="lg"
                              autoComplete="new-password"
                              error={meta.errors.length > 0}
                              onChange={(e) => {
                                control.onChange(e);
                                this.setState({ newPassword: e.target.value });
                              }}
                              rightUnit={
                                <KuikaSvg
                                  name={this.state.showPassword ? "eye" : "eye-closed"}
                                  pointer
                                  onClick={() => this.setState({ showPassword: !this.state.showPassword })}
                                />
                              }
                            />
                            <div style={{ minHeight: "20px" }}>
                              {meta.errors.length > 0 && (
                                <KuikaSmallMediumText style={{ color: "var(--red-5)" }}>
                                  {meta.errors[0]}
                                </KuikaSmallMediumText>
                              )}
                            </div>
                          </div>
                        )}
                      </Field>

                      <Field
                        name="confirmPassword"
                        rules={[{ required: true, message: "Please confirm your password!" }]}
                      >
                        {(control, meta) => (
                          <div style={{ width: "100%" }}>
                            <KuikaInput
                              {...control}
                              type={this.state.showConfirmPassword ? "text" : "password"}
                              leftIcon={<KuikaSvg name="lock" />}
                              placeholder="Confirm password"
                              size="lg"
                              autoComplete="new-password"
                              error={meta.errors.length > 0}
                              onChange={(e) => {
                                control.onChange(e);
                                this.setState({ confirmPassword: e.target.value });
                              }}
                              rightUnit={
                                <KuikaSvg
                                  name={this.state.showConfirmPassword ? "eye" : "eye-closed"}
                                  pointer
                                  onClick={() =>
                                    this.setState({ showConfirmPassword: !this.state.showConfirmPassword })
                                  }
                                />
                              }
                            />
                            <div style={{ minHeight: "20px" }}>
                              {meta.errors.length > 0 && (
                                <KuikaSmallMediumText style={{ color: "var(--red-5)" }}>
                                  {meta.errors[0]}
                                </KuikaSmallMediumText>
                              )}
                              {this.state.resetPasswordErrorMessage && !meta.errors.length && (
                                <KuikaSmallMediumText style={{ color: "var(--red-5)" }}>
                                  {this.state.resetPasswordErrorMessage}
                                </KuikaSmallMediumText>
                              )}
                            </div>
                          </div>
                        )}
                      </Field>
                    </div>

                    <div className="email-confirmation__return-btn">
                      <KuikaButton
                        htmlType="submit"
                        size="lg"
                        status={this.getSubmitStatus()}
                        statusLabels={CHANGE_PASSWORD_STATUS_LABELS}
                        disabled={!this.state.newPassword || !this.state.confirmPassword}
                        style={{ width: "100%" }}
                        uppercase
                      >
                        Change password
                      </KuikaButton>
                    </div>

                    <KuikaButton type="tertiary" size="lg" uppercase onClick={this.goToLogin} style={{ width: "100%" }}>
                      Return to sign in
                    </KuikaButton>
                  </Form>
                </>
              )}
            </div>
          </div>

          {!isMainAI && (
            <div className="email-confirmation__bottom-section">
              <KuikaButton
                type="text"
                size="sm"
                uppercase
                onClick={() => window.open("https://www.kuika.com/en/legal/privacy", "_blank")}
              >
                Privacy
              </KuikaButton>
              <KuikaButton
                type="text"
                size="sm"
                uppercase
                onClick={() => window.open("https://www.kuika.com/en/legal/terms", "_blank")}
              >
                Terms
              </KuikaButton>
            </div>
          )}
        </div>
      </div>
    );
  }
}

const resetPassword = withHistory<IResetPasswordProps>(ResetPassword);
export { resetPassword as ResetPassword };
